import store from 'store/Store';
import languageLocaleUtils from 'utils/LanguageLocale';
import { TIME_OUT } from 'constants/location';
import Actions from 'Actions';
import { NEXT_DAY_DELIVERY_MESSAGE } from 'constants/TestTarget';
import userUtils from 'utils/User';
import Empty from 'constants/empty';
import Location from 'utils/Location';
import dateUtils from 'utils/Date';
import ExperienceLocationEN from 'components/Happening/ExperienceLocation/locales/ExperienceLocation_en_US';
import ExperienceLocationFR from 'components/Happening/ExperienceLocation/locales/ExperienceLocation_fr_CA';
import DeliveryOptionsEN from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_en_US';
import DeliveryOptionsFR from 'components/ProductPage/DeliveryOptions/locales/DeliveryOptions_fr_CA';

const { getLocalisedDataFromResource, isFrench, isCanada: isCA, isUS } = languageLocaleUtils;
const getDeliveryOptionsText = getLocalisedDataFromResource(DeliveryOptionsEN, DeliveryOptionsFR);

function showStoreListModal(e, options = {}) {
    e.preventDefault();
    e.stopPropagation();
    const getExperienceText = getLocalisedDataFromResource(ExperienceLocationEN, ExperienceLocationFR);
    const isFromChangeMethod = options?.isFromChangeMethod;

    const {
        item, callback, mountCallback, cancelCallback, closeChangeMethodModalCallback
    } = options;
    const currentProduct = this.props.currentProduct || {
        currentSku: item?.sku,
        productDetails: {
            brandName: item?.sku?.brandName,
            displayName: item?.sku?.displayName,
            productId: item?.sku?.productId
        }
    };
    const entry = options?.entry || (Location.isProductPage() ? 'ppage' : '');

    if (navigator.geolocation) {
        const actionInfo = {
            isOpen: true,
            entry,
            currentProduct: currentProduct,
            isRopisSelected: this.props.reserveAndPickup,
            fromChooseOptionsModal: this.props.fromChooseOptionsModal,
            fromChangeMethodModal: isFromChangeMethod,
            ...(isFromChangeMethod && {
                disableNonBopisStores: true,
                disableOutOfStockStores: true,
                callback: callback,
                mountCallback: mountCallback,
                cancelCallback: cancelCallback,
                closeChangeMethodModalCallback: closeChangeMethodModalCallback
            })
        };
        navigator.geolocation.getCurrentPosition(
            position => {
                actionInfo.location = position;
                store.dispatch(Actions.showReserveAndPickUpModal(actionInfo));
            },
            () => {
                store.dispatch(
                    Actions.showInfoModal({
                        isOpen: true,
                        title: getExperienceText('locationSharingDisabled'),
                        message: getExperienceText('locationUpdateSettings'),
                        buttonText: getExperienceText('ok'),
                        callback: () => {
                            store.dispatch(Actions.showReserveAndPickUpModal(actionInfo));
                        }
                    })
                );
            },
            { timeout: TIME_OUT }
        );
    }
}

function getCountdownMessage(isBopis, hours, minutes, startingPrice) {
    let countDownMessage;

    if (isBopis) {
        countDownMessage = hours ? `Order within ${hours} hours ${minutes} min, ready today` : `Order within ${minutes} min, ready today`;
    } else {
        const startingPriceString = startingPrice ? `, starting at $${startingPrice}` : '';
        countDownMessage = hours
            ? `Order within ${hours} hours ${minutes} min, get it today${startingPriceString}`
            : `Order within ${minutes} min, get it today${startingPriceString}`;
    }

    return countDownMessage;
}

function getUsersTime() {
    const currentUserTime = new Date();
    const currentUserTimeHours = currentUserTime.getHours();
    const currentUserTimeMinutes = currentUserTime.getMinutes();

    return {
        currentUserTimeHours,
        currentUserTimeMinutes
    };
}

function getStringWithTrimmedPrice(msg) {
    if (!msg) {
        return '';
    }

    const index = isFrench() ? msg.indexOf(' à partir') : msg.indexOf(', starting at');
    const trimmedString = msg.slice(0, index);

    return index > -1 ? trimmedString : msg;
}

function getBOPISCountdownMessage(bopisMessage) {
    const hrsBOPIS = bopisMessage?.match(/[0-9]+:[0-9]+/)?.[0].split(':');
    let pickupMessageCountdown = bopisMessage;

    if (hrsBOPIS) {
        const { currentUserTimeHours, currentUserTimeMinutes } = getUsersTime();

        const convertedStoreTime = Number(hrsBOPIS[0]);
        const limitTime = convertedStoreTime !== 12 ? convertedStoreTime + 12 : convertedStoreTime;
        let totalHoursBOPIS = ((limitTime - currentUserTimeHours) * 60 - currentUserTimeMinutes) / 60;
        let hours = Math.floor(totalHoursBOPIS);
        let minutes = Math.floor((totalHoursBOPIS % 1) * 60);

        if (totalHoursBOPIS < 0) {
            totalHoursBOPIS = ((24 - currentUserTimeHours) * 60 - currentUserTimeMinutes) / 60;
            hours = limitTime + Math.floor(totalHoursBOPIS);
            minutes = Math.floor((totalHoursBOPIS % 1) * 60);
        }

        pickupMessageCountdown = getCountdownMessage(true, hours, minutes);
    }

    return pickupMessageCountdown;
}

function getSDDCountdownMessage(msg) {
    if (msg?.match(NEXT_DAY_DELIVERY_MESSAGE.ORDER_NOW) || msg?.match(NEXT_DAY_DELIVERY_MESSAGE.ORDER_TODAY)) {
        return msg;
    }

    const storeOrderTimeLimit = msg?.match(/[0-9]+/);
    let sameDayDeliveryMessageCountdown = msg;

    if (storeOrderTimeLimit) {
        const convertedStoreTime = Number(storeOrderTimeLimit);
        const limitTime = convertedStoreTime !== 12 ? convertedStoreTime + 12 : convertedStoreTime;

        const { currentUserTimeHours, currentUserTimeMinutes } = getUsersTime();

        let totalHours = ((limitTime - currentUserTimeHours) * 60 - currentUserTimeMinutes) / 60;
        const startingPrice = msg.split(/\$/)[1];
        let hours = Math.floor(totalHours);
        let minutes = Math.floor((totalHours % 1) * 60);

        // validate if the totalHours is valid, example client ordering from CA and choosing NY store
        if (totalHours < 0) {
            totalHours = ((24 - currentUserTimeHours) * 60 - currentUserTimeMinutes) / 60;
            hours = limitTime + Math.floor(totalHours);
            minutes = Math.floor((totalHours % 1) * 60);
        }

        sameDayDeliveryMessageCountdown = getCountdownMessage(false, hours, minutes, startingPrice);
    }

    return sameDayDeliveryMessageCountdown;
}

function isSDUAllowedForUser(userSubscriptions) {
    const SDUSubscription = userSubscriptions?.filter(subscription => subscription.type === 'SDU') || Empty.Array;
    const isUserSDUTrialEligible = userUtils.isAnonymous() ? true : SDUSubscription.length > 0 && SDUSubscription[0]?.isTrialEligible;
    const hasUserSDUSubscribed = SDUSubscription.length > 0 && SDUSubscription[0].status === 'ACTIVE';

    return isUserSDUTrialEligible && !hasUserSDUSubscribed;
}

const closeSDULandingPageModal = () => {
    store.dispatch(Actions.showSDULandingPageModal({ isOpen: false }));
};

function showSDULandingPageModal(options = {}) {
    const {
        isOpen = true,
        mediaId,
        skuTrialPeriod,
        isSDUAddedToBasket = false,
        isUserSDUTrialEligible = false,
        isCanada = false,
        skipConfirmationModal = false,
        isUserSDUTrialAllowed = false,
        fromChooseOptionsModal = false
    } = options;

    store.dispatch(
        Actions.showSDULandingPageModal({
            isOpen,
            sduMediaId: mediaId,
            skuTrialPeriod,
            isSDUAddedToBasket,
            isUserSDUTrialEligible,
            isCanada,
            skipSDUConfirmationModal: skipConfirmationModal,
            isUserSDUTrialAllowed,
            closeSDULandingPageModal,
            fromChooseOptionsModal
        })
    );
}

/**
 * Checks if Next-Day Delivery (NDD) feature is enabled for the current country (GUAR-7834).
 *
 * This is the primary kill switch for NDD eligibility evaluation. When OFF, all NDD-related
 * features are disabled and the system falls back to SDD-only behavior.
 *
 * @returns {boolean} - true if NDD is enabled for current country, false otherwise
 *
 * **Configuration Structure** (country-specific):
 * - **Path**: `Sephora.configurationSettings.nextDayDelivery`
 * - **US**: `nextDayDelivery.nddEnabledUS.isEnabled`
 * - **CA**: `nextDayDelivery.nddEnabledCA.isEnabled`
 *
 * **Usage**:
 * - Use this flag to control whether NDD eligibility should be evaluated
 * - When false, ignore `isNddEligibleSku` and treat all scenarios as SDD-only
 * - When true, evaluate both SDD and NDD eligibility normally
 *
 * @example
 * const nddEnabled = isNDDEnabled();
 * const effectiveNddEligible = nddEnabled && currentSku.isNddEligibleSku;
 */
function isNDDEnabled() {
    if (typeof Sephora === 'undefined') {
        return false;
    }

    const nddConfig = Sephora?.configurationSettings?.nextDayDelivery;
    const configKey = isCA() ? 'nddEnabledCA' : isUS() ? 'nddEnabledUS' : null;

    if (nddConfig && configKey && nddConfig[configKey]) {
        const { isEnabled } = nddConfig[configKey];

        return Boolean(isEnabled);
    }

    return false;
}

/**
 * Checks if "Fast Delivery" branding should be used instead of "Same-Day Delivery" (GUAR-7834).
 *
 * Controls the display text for the delivery tile title. When enabled, shows "Fast Delivery"
 * instead of "Same-Day Delivery" to align with NDD initiative branding.
 *
 * @returns {boolean} - true if "Fast Delivery" branding should be used, false for "Same-Day Delivery"
 *
 * **Configuration Structure** (country-specific):
 * - **Path**: `Sephora.configurationSettings.nextDayDelivery`
 * - **US**: `nextDayDelivery.nddNewDeliveryNameUS.isEnabled`
 * - **CA**: `nextDayDelivery.nddNewDeliveryNameCA.isEnabled`
 *
 * **Usage**:
 * - Use this flag to determine which locale key to display for tile title
 * - When true: Use `getText('fastDelivery')` → "Fast Delivery"
 * - When false: Use `sameDayTitle` from API or `getText('sameDayDelivery')` → "Same-Day Delivery"
 *
 * **Note**: This flag is independent of `isNDDEnabled()` and only controls display text,
 * not business logic or eligibility evaluation.
 *
 * @example
 * const titleText = isNDDNewDeliveryName()
 *     ? getText('fastDelivery')
 *     : (sameDayTitle || getText('sameDayDelivery'));
 */
function isNDDNewDeliveryName() {
    if (typeof Sephora === 'undefined') {
        return false;
    }

    const nddConfig = Sephora?.configurationSettings?.nextDayDelivery;
    const configKey = isCA() ? 'nddNewDeliveryNameCA' : isUS() ? 'nddNewDeliveryNameUS' : null;

    if (nddConfig && configKey && nddConfig[configKey]) {
        const { isEnabled } = nddConfig[configKey];

        return Boolean(isEnabled);
    }

    return false;
}

// GUAR-8552: US matches `12345` or `12345-6789`; CA matches canonical postal code
// (rejects D/F/I/O/Q/U as first letter per Canada Post spec).
const US_ZIP_RE = /^\d{5}(-\d{4})?$/;
const CA_ZIP_RE = /^(?!.*[DFIOQU])[A-VXY]\d[A-Z] ?\d[A-Z]\d$/i;

/**
 * Validate + normalize a zipcode before appending to an outbound PXS request (GUAR-8552).
 *
 * US: strips the optional Zip+4 suffix and pads to 5 digits with leading zeros.
 * CA: canonicalizes to uppercase `A1A 1A1` (three-space-three).
 * Returns `null` when the input is missing, not a string, or does not match the country format —
 * callers drop the query param entirely to avoid fragmenting cache keys on invalid values.
 *
 * @param {*} zipcode - Raw zipcode value from user state or storage.
 * @returns {string|null} - Normalized zipcode or `null` when invalid.
 */
function normalizeAndValidateZipCode(zipcode) {
    if (typeof zipcode !== 'string') {
        return null;
    }

    const trimmed = zipcode.trim();

    if (!trimmed) {
        return null;
    }

    if (isCA()) {
        if (!CA_ZIP_RE.test(trimmed)) {
            return null;
        }

        const compact = trimmed.replace(/\s+/g, '').toUpperCase();

        return `${compact.slice(0, 3)} ${compact.slice(3)}`;
    }

    if (!US_ZIP_RE.test(trimmed)) {
        return null;
    }

    return trimmed.split('-')[0].padStart(5, '0');
}

/**
 * Strips HTML tags and markup tokens from PXS messages (GUAR-7783 defense).
 * Handles future markup like <b>tomorrow</b>, <span style="color:red">today</span>.
 *
 * @param {string} text - Raw text from PXS
 * @returns {string} - Clean text with markup removed
 */
function stripMarkup(text) {
    if (!text || typeof text !== 'string') {
        return '';
    }

    // Remove HTML tags: <b>, <span>, etc.
    // Pattern: /<[^>]+>/g matches any HTML tag
    return text.replace(/<[^>]+>/g, '').trim();
}

/**
 * Detects pre/post cutoff state from PXS message strings (GUAR-7610).
 * UFE does not calculate cutoff logic independently - we parse PXS messages.
 *
 * DEFENSIVE (GUAR-7783): Strips markup before matching, uses locale-aware term matching.
 *
 * @param {string} message - Delivery message from PXS (may contain markup)
 * @param {string} type - 'SDD' or 'NDD'
 * @param {Function} getText - Localization function for term matching
 * @returns {boolean} - true if pre-cutoff, false if post-cutoff or invalid
 */
function isPreCutoff(message, type, getText) {
    if (!message || typeof message !== 'string') {
        return false;
    }

    // GUAR-7783 defense: Strip HTML tags/markup before parsing
    const cleanMessage = stripMarkup(message).toLowerCase();

    if (type === 'SDD') {
        // SDD pre-cutoff: message contains "today" (locale-aware)
        // Get locale-specific "today" term for matching
        const todayTerm = (getText('asSoonAsToday') || 'today').toLowerCase().split(' ').pop();

        // Example: "Order within 2 hours 35 min, get it <b>today</b>, starting at $6.95"
        // After stripMarkup: "Order within 2 hours 35 min, get it today, starting at $6.95"
        return cleanMessage.includes(todayTerm);
    } else if (type === 'NDD') {
        // NDD pre-cutoff: message contains "tomorrow" (locale-aware)
        // Get locale-specific "tomorrow" term for matching
        const tomorrowTerm = (getText('asSoonAsTomorrow') || 'tomorrow').toLowerCase().split(' ').pop();

        // Example: "Order now, get it <b>tomorrow</b>, starting at $6.95"
        // After stripMarkup: "Order now, get it tomorrow, starting at $6.95"
        return cleanMessage.includes(tomorrowTerm);
    }

    return false;
}

/**
 * Extracts and formats date from nddDeliveryLabel for display (GUAR-7610).
 *
 * DEFENSIVE (GUAR-7783): Strips markup, handles variable formats, year wrapping.
 *
 * @param {string} nddDeliveryLabel - PXS label like "by 9 pm, Feb 13" (may contain markup)
 * @param {Function} getText - Localization function
 * @returns {string|null} - Formatted subtitle "As soon as Wed, Feb 13" or null
 */
function extractAndFormatDate(nddDeliveryLabel, getText) {
    if (!nddDeliveryLabel || typeof nddDeliveryLabel !== 'string') {
        return null;
    }

    // GUAR-7783 defense: Strip HTML tags/markup before parsing
    // Example: "by 9 pm, <b>Feb 13</b>" → "by 9 pm, Feb 13"
    const cleanLabel = stripMarkup(nddDeliveryLabel);

    // Parse date string from label
    // Expected format: "by 9 pm, Feb 13" or similar
    // DEFENSIVE: Match month (3 letters) followed by day (1-2 digits)
    // Handles: "Feb 13", "février 13", "13 février" (FR-CA reversed order)
    // Uses \p{L} to match any Unicode letter (covers all French accents: é, è, à, ô, û, ç, etc.)
    const dateMatch = cleanLabel.match(/(\p{L}{3,10})\s+(\d{1,2})|(\d{1,2})\s+(\p{L}{3,10})/u);

    if (!dateMatch) {
        // Edge case: nddDeliveryLabel exists but unparseable → graceful null
        return null;
    }

    // Extract month and day (handle both "Feb 13" and "13 Feb" orders)
    let month, day;

    if (dateMatch[1]) {
        // Format: "Feb 13"
        month = dateMatch[1];
        day = dateMatch[2];
    } else {
        // Format: "13 Feb"
        day = dateMatch[3];
        month = dateMatch[4];
    }

    // Construct date object with year wrapping logic
    // Month map supports both EN and FR month names
    const monthMap = {
        // EN months
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11,
        // FR months (abbreviated)
        jan: 0,
        fév: 1,
        mar: 2,
        avr: 3,
        mai: 4,
        juin: 5,
        juil: 6,
        août: 7,
        sept: 8,
        oct: 9,
        nov: 10,
        déc: 11,
        // FR full month names (first 3 chars)
        janvier: 0,
        février: 1,
        mars: 2,
        avril: 3,
        juillet: 6,
        septembre: 8,
        octobre: 9,
        novembre: 10,
        décembre: 11
    };

    // Case-insensitive month lookup
    const monthKey = Object.keys(monthMap).find(key => key.toLowerCase() === month.toLowerCase().substring(0, key.length));
    const monthIndex = monthKey ? monthMap[monthKey] : undefined;

    if (monthIndex === undefined) {
        // Month not recognized → graceful null
        return null;
    }

    // Year wrapping logic: If parsed month is earlier than current month, wrap to next year
    // Example: We're in November (month 10), but parsing "Feb 13" (month 1) → use next year
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    let targetYear = currentYear;

    if (monthIndex < currentMonth) {
        // Month has passed this year → must be next year
        targetYear = currentYear + 1;
    }

    const deliveryDate = new Date(targetYear, monthIndex, parseInt(day, 10));

    // Special case: If parsed date is tomorrow, use "As soon as tomorrow"
    if (dateUtils.isTomorrow(deliveryDate.toISOString())) {
        return getText('asSoonAsTomorrow');
    }

    // Format date as "Wed, Feb 13" (EN) or "mer le 13 février" (FR)
    const formattedDate = dateUtils.getPromiseDate(deliveryDate, false);

    return getText('asSoonAs', [formattedDate]);
}

/**
 * Generates the tile summary SLA subtitle for Fast Delivery based on PXS currentSku data (GUAR-7610).
 *
 * Implements rendering logic for 9 acceptance criteria scenarios by parsing PXS strings.
 *
 * DEFENSIVE (GUAR-7783): All parsing utilities strip markup and handle variable formats.
 *
 * Scenarios:
 * - Scenario 1: SDD only, pre-cutoff → "As soon as today"
 * - Scenario 2: SDD only, post-cutoff → "As soon as tomorrow"
 * - Scenario 3: NDD only, pre-cutoff → "As soon as tomorrow"
 * - Scenario 4: NDD only, post-cutoff → "As soon as [formatted date]"
 * - Scenario 5: Both SDD+NDD → Follows SDD rules (fastest)
 * - Scenario 6: Neither SDD nor NDD → Returns null (no subtitle)
 * - Scenario 7: SDU/non-SDU → Identical output (no subscription logic here)
 * - Scenario 8: Date formatting → "Wed, Feb 13" format via dateUtils
 * - Scenario 9: Responsive → Works on desktop and mobile (handled by component)
 *
 * @param {Object} currentSku - Current SKU object from Redux state (product.currentSku)
 * @param {boolean} currentSku.isSameDayEligibleSku - SKU supports SDD
 * @param {string} currentSku.sameDayDeliveryMessage - SDD message from PXS (may contain markup)
 * @param {boolean} currentSku.isNddEligibleSku - SKU supports NDD
 * @param {string} currentSku.nddDeliveryMessage - NDD message from PXS (may contain markup)
 * @param {string} currentSku.nddDeliveryLabel - NDD date label from PXS (may contain markup)
 * @param {Function} getText - Localization function from getLocalisedDataFromResource
 * @returns {string|null} - Subtitle text or null if no subtitle should be shown
 */
function getFastDeliveryTileSummarySubtitle(currentSku, getText) {
    // Guard: No SKU data → return null
    if (!currentSku) {
        return null;
    }

    // GUAR-8552: Defensive short-circuit for PXS-provided SLA summary at the sku level.
    // Product-root `fastDeliverySLASummary` is the primary path (handled by the caller in
    // computeSameDaySubTitle); this branch guards against future BE placements at the sku level.
    if (typeof currentSku.fastDeliverySLASummary === 'string' && currentSku.fastDeliverySLASummary.trim() !== '') {
        return currentSku.fastDeliverySLASummary;
    }

    const {
        isSameDayEligibleSku, sameDayDeliveryMessage, isNddEligibleSku, nddDeliveryMessage, nddDeliveryLabel
    } = currentSku;

    // CRITICAL: Check MCH NDD eligibility flag (GUAR-7644)
    // If flag is OFF, force SDD-only behavior (ignore NDD eligibility)
    const nddEnabled = isNDDEnabled();
    const effectiveNddEligible = nddEnabled && isNddEligibleSku;

    // Scenario 6: Neither SDD nor NDD → No subtitle
    if (!isSameDayEligibleSku && !effectiveNddEligible) {
        return null;
    }

    // Scenario 5: Both SDD and NDD → Follow SDD rules (fastest potential option)
    // Scenario 1: SDD only, pre-cutoff → "As soon as today"
    if (isSameDayEligibleSku && isPreCutoff(sameDayDeliveryMessage, 'SDD', getText)) {
        return getText('asSoonAsToday');
    }

    // Scenario 2: SDD only, post-cutoff → "As soon as tomorrow"
    if (isSameDayEligibleSku && !isPreCutoff(sameDayDeliveryMessage, 'SDD', getText)) {
        return getText('asSoonAsTomorrow');
    }

    // Scenario 3: NDD only, pre-cutoff → "As soon as tomorrow"
    if (effectiveNddEligible && isPreCutoff(nddDeliveryMessage, 'NDD', getText)) {
        return getText('asSoonAsTomorrow');
    }

    // Scenario 4: NDD only, post-cutoff → "As soon as [DATE]"
    // Edge case (GUAR-7783 caveat #3): If nddDeliveryMessage doesn't contain "tomorrow"
    // but nddDeliveryLabel is null/empty, extractAndFormatDate returns null → graceful fallback
    if (effectiveNddEligible && !isPreCutoff(nddDeliveryMessage, 'NDD', getText)) {
        const formattedSubtitle = extractAndFormatDate(nddDeliveryLabel, getText);

        // May return null if nddDeliveryLabel is unparseable → fallback to Scenario 6
        return formattedSubtitle;
    }

    // Fallback: No subtitle (Scenario 6)
    return null;
}

// Helper: Get delivery pricing text for A/B test (TS-4006)
const getDeliveryPricingText = (
    showDeliveryPrice,
    sameDayAvailable,
    currentSku,
    sddTilePromoMessage,
    isUserSDUSubscriber,
    isSDUAddedToBasket,
    isSameDayCouponApplied
) => {
    const displayDeliveryPrice = showDeliveryPrice && sameDayAvailable && currentSku?.isSameDayEligibleSku;

    if (!displayDeliveryPrice) {
        return null;
    }

    const shouldHidePricing = !!sddTilePromoMessage || isUserSDUSubscriber || isSDUAddedToBasket || isSameDayCouponApplied;

    if (shouldHidePricing) {
        return null;
    }

    const pricingKey = isCA() ? 'sduPricingCA' : 'sduPricingUS';

    return getDeliveryOptionsText(pricingKey);
};

// Helper: derive sameDay subtitle (mirrors sameDaySubTitleSelector combiner)
const computeSameDaySubTitle = (
    product,
    sameDayAvailable,
    isUserSDUSubscriber,
    isUserSDUTrialEligible,
    showDeliveryPrice,
    isSDUAddedToBasket,
    isSameDayCouponApplied
) => {
    const sddTilePromoMessage = product?.currentProductUserSpecificDetails?.sddTilePromoMessage;
    const { currentSku } = product;
    let subtitle = '';

    // A/B Test: Delivery pricing (TS-4006)
    const pricingText = getDeliveryPricingText(
        showDeliveryPrice,
        sameDayAvailable,
        currentSku,
        sddTilePromoMessage,
        isUserSDUSubscriber,
        isSDUAddedToBasket,
        isSameDayCouponApplied
    );

    if (pricingText) {
        return pricingText;
    }

    // Priority 1: Promo message takes precedence (existing logic)
    if (sddTilePromoMessage) {
        const isUserEligibleForSDDPromo = sameDayAvailable && (!isUserSDUSubscriber || isUserSDUTrialEligible);

        if (isUserEligibleForSDDPromo) {
            return sddTilePromoMessage;
        }
    }

    // Priority 2: SLA subtitle (GUAR-7594; SDU parity per GUAR-8552 / GUAR-7610 Scenario 7).
    // PXS delivers `fastDeliverySLASummary` on the PRODUCT ROOT (sibling to currentSku).
    // Falls back to the client-side 9-scenario logic when the field is absent (guests without
    // a saved zip, pre-rollout SKUs) so the tile never regresses to blank during rollout.
    if (isNDDEnabled() && sameDayAvailable) {
        if (typeof product?.fastDeliverySLASummary === 'string' && product.fastDeliverySLASummary.trim() !== '') {
            return product.fastDeliverySLASummary;
        }

        const slaSubtitle = getFastDeliveryTileSummarySubtitle(currentSku, getDeliveryOptionsText);

        if (slaSubtitle) {
            return slaSubtitle;
        }
    }

    // Priority 3: Not available message (existing logic)
    if (!currentSku.isSameDayEligibleSku) {
        subtitle = getDeliveryOptionsText(isNDDEnabled() ? 'fastDeliveryNotAvailable' : 'sameDayDeliveryNotAvailable');
    }

    return subtitle;
};

export default {
    showStoreListModal,
    getCountdownMessage,
    getUsersTime,
    getBOPISCountdownMessage,
    getSDDCountdownMessage,
    isSDUAllowedForUser,
    getStringWithTrimmedPrice,
    showSDULandingPageModal,
    isNDDEnabled,
    isNDDNewDeliveryName,
    getFastDeliveryTileSummarySubtitle,
    computeSameDaySubTitle,
    normalizeAndValidateZipCode
};
